import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Lock, Crown } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cache the /subscription/me response for the session (module-scoped, cleared
// only on hard reload). Prevents every TierGate on a page from hammering the
// endpoint.
let _cachedSub = null;
let _cachedSubAt = 0;
const SUB_CACHE_MS = 30_000;

const fetchSubscription = async () => {
  const now = Date.now();
  if (_cachedSub && (now - _cachedSubAt) < SUB_CACHE_MS) return _cachedSub;
  try {
    const res = await axios.get(`${API}/subscription/me`, { withCredentials: true });
    _cachedSub = res.data;
    _cachedSubAt = now;
    return res.data;
  } catch { return null; }
};

// Public escape hatch for other components to invalidate the cache after a
// successful upgrade / tier change.
export const invalidateTierCache = () => { _cachedSub = null; _cachedSubAt = 0; };

/**
 * Wraps content that requires a specific tier OR a specific feature flag.
 * Shows a paywall overlay for users who don't have access. Content is rendered
 * but blurred/locked underneath.
 *
 * @param {string} feature - Display name (e.g., "Dynamic Lighting")
 * @param {string} requiredTier - Minimum tier: "adventurer" | "archmage"
 * @param {string} featureKey - Optional backend feature key (e.g. "vtt_live",
 *   "dynamic_lighting"). If provided, we check /subscription/me.features which
 *   honours grandfathered_features — so pre-Feb-2026 users keep access.
 * @param {string} description - Why to upgrade
 * @param {React.ReactNode} children - The actual page content
 */
export const TierGate = ({ feature, requiredTier = "adventurer", featureKey, description, children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const userTier = user?.tier || "commoner";
  const [sub, setSub] = useState(_cachedSub);
  const [subLoading, setSubLoading] = useState(!!featureKey && !_cachedSub);

  useEffect(() => {
    if (!featureKey) return;
    let cancelled = false;
    fetchSubscription().then(s => { if (!cancelled) { setSub(s); setSubLoading(false); } });
    return () => { cancelled = true; };
  }, [featureKey]);

  const tierOrder = { commoner: 0, adventurer: 1, archmage: 2 };
  const tierAccess = tierOrder[userTier] >= tierOrder[requiredTier];
  // If a featureKey was provided, prefer the backend's effective flag (which
  // includes grandfathered). Fall back to plain tier comparison.
  const featureAccess = featureKey ? !!sub?.features?.[featureKey] : tierAccess;
  const hasAccess = featureKey ? featureAccess : tierAccess;

  // While we're still loading /subscription/me for a featureKey gate, render
  // the children optimistically. Prevents a flash of the paywall for
  // grandfathered users. Backend gates are the real source of truth.
  if (featureKey && subLoading) return children;
  if (hasAccess) return children;

  const tierLabel = requiredTier === "archmage" ? "Archmage" : "Adventurer";

  return (
    <div className="relative" data-testid="tier-gate-overlay">
      {/* Blurred preview of actual content */}
      <div className="pointer-events-none select-none filter blur-[3px] opacity-40 max-h-[60vh] overflow-hidden">
        {children}
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 flex items-start justify-center pt-24 z-10">
        <div className="bg-card border border-border/80 rounded-sm p-8 max-w-md text-center shadow-2xl" data-testid="tier-gate-card">
          <div className="w-14 h-14 rounded-full bg-amber-600/20 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-amber-400" />
          </div>
          <h2 className="text-2xl font-serif text-foreground mb-2">{feature}</h2>
          <p className="text-muted-foreground text-sm mb-1">
            {description || `This feature is available on the ${tierLabel} tier and above.`}
          </p>
          <p className="text-xs text-muted-foreground mb-6">
            Upgrade to unlock full access — starting at less than £1.70/month.
          </p>
          <Button
            onClick={() => navigate("/subscription")}
            className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm px-8"
            data-testid="tier-gate-upgrade-button"
          >
            <Crown className="w-4 h-4 mr-2" /> Upgrade to {tierLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};
